/**
 * utils/fieldTypeMeta.tsx
 *
 * Shared lookup map for form field types: maps each type key to its icon
 * component and French display label. Used across components that need to
 * render a field type consistently.
 * Updated: added single_choice entry; added 'xs' size variant (w-3.5 h-3.5).
 * Updated Apr 2026: added 'info' type (display-only informational message).
 */

import { Upload, Type, MessageSquare, ListChecks, Calendar, FileText, CircleDot, Info } from 'lucide-react';

interface FieldTypeMeta {
  icon: (size: 'xs' | 'sm' | 'md') => React.ReactElement;
  label: string;
}

export const FIELD_TYPE_META: Record<string, FieldTypeMeta> = {
  document: {
    icon: (size) => {
      if (size === 'xs') return <Upload className="w-3.5 h-3.5 text-primary-500" />;
      if (size === 'sm') return <Upload className="w-4 h-4 text-primary-500" />;
      return <Upload className="w-5 h-5 text-primary-600" />;
    },
    label: 'Document',
  },
  short_text: {
    icon: (size) => {
      if (size === 'xs') return <Type className="w-3.5 h-3.5 text-sky-500" />;
      if (size === 'sm') return <Type className="w-4 h-4 text-sky-500" />;
      return <Type className="w-5 h-5 text-sky-600" />;
    },
    label: 'Texte court',
  },
  text: {
    icon: (size) => {
      if (size === 'xs') return <MessageSquare className="w-3.5 h-3.5 text-primary-500" />;
      if (size === 'sm') return <MessageSquare className="w-4 h-4 text-primary-500" />;
      return <MessageSquare className="w-5 h-5 text-primary-600" />;
    },
    label: 'Texte long',
  },
  single_choice: {
    icon: (size) => {
      if (size === 'xs') return <CircleDot className="w-3.5 h-3.5 text-primary-500" />;
      if (size === 'sm') return <CircleDot className="w-4 h-4 text-primary-500" />;
      return <CircleDot className="w-5 h-5 text-primary-600" />;
    },
    label: 'Choix unique',
  },
  multiple_choice: {
    icon: (size) => {
      if (size === 'xs') return <ListChecks className="w-3.5 h-3.5 text-primary-500" />;
      if (size === 'sm') return <ListChecks className="w-4 h-4 text-primary-500" />;
      return <ListChecks className="w-5 h-5 text-primary-600" />;
    },
    label: 'Choix multiples',
  },
  date: {
    icon: (size) => {
      if (size === 'xs') return <Calendar className="w-3.5 h-3.5 text-primary-500" />;
      if (size === 'sm') return <Calendar className="w-4 h-4 text-primary-500" />;
      return <Calendar className="w-5 h-5 text-primary-600" />;
    },
    label: 'Date',
  },
  info: {
    icon: (size) => {
      if (size === 'xs') return <Info className="w-3.5 h-3.5 text-primary-500" />;
      if (size === 'sm') return <Info className="w-4 h-4 text-primary-500" />;
      return <Info className="w-5 h-5 text-primary-600" />;
    },
    label: 'Message informatif',
  },
};

const FALLBACK_META: FieldTypeMeta = {
  icon: (size) => {
    if (size === 'xs') return <FileText className="w-3.5 h-3.5 text-neutral-400" />;
    if (size === 'sm') return <FileText className="w-4 h-4 text-neutral-400" />;
    return <FileText className="w-5 h-5 text-neutral-400" />;
  },
  label: 'Champ',
};

export function getFieldTypeMeta(type: string): FieldTypeMeta {
  return FIELD_TYPE_META[type] ?? FALLBACK_META;
}
